<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useNotificationsStore } from '@/stores/notifications'

/* Store-driven live region (not a Reka toast): notifications are announced
   politely to screen readers and never steal focus. */
const store = useNotificationsStore()
const { t, te } = useI18n()

function text(message: string): string {
  return te(message) ? t(message) : message
}
</script>

<template>
  <div class="toasts" role="status" aria-live="polite">
    <div v-for="item in store.items" :key="item.id" class="toast" :class="`toast--${item.kind}`">
      <span>
        <!-- kind is also color-coded; announce it for non-visual users -->
        <span class="visually-hidden">{{ t(`common.${item.kind}`) }}: </span>
        {{ text(item.message) }}
      </span>
      <button
        type="button"
        class="toast__dismiss"
        :aria-label="t('common.close')"
        @click="store.dismiss(item.id)"
      >
        &times;
      </button>
    </div>
  </div>
</template>

<style scoped>
.toasts {
  position: fixed;
  bottom: var(--space-4);
  right: var(--space-4);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-width: min(90vw, 380px);
}

.toast {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-left-width: 4px;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
}

.toast--error {
  border-left-color: var(--color-error);
}

.toast--success {
  border-left-color: var(--color-success);
}

.toast--info {
  border-left-color: var(--color-accent);
}

.toast__dismiss {
  min-width: var(--size-touch);
  min-height: var(--size-touch);
  border: 0;
  background: transparent;
  font-size: var(--font-size-lg);
  cursor: pointer;
}
</style>
